import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Button } from "@shared/components/ui/button";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { Badge } from "@shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Progress } from "@shared/components/ui/progress";
import {
  Package,
  Truck,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  MapPin,
  User,
  Phone,
  Calendar,
  Navigation,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import * as packagesService from "../services/packagesService";
import { ENHANCED_LIVREUR_STATUS_OPTIONS as STATUS_OPTIONS_DATA, QUICK_ACTIONS as QUICK_ACTIONS_DATA, PACKAGE_QUERY_KEYS } from "../constants";

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
  Package,
  Truck,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  MapPin,
  Navigation,
};

// Enhanced status options with workflow progression
const ENHANCED_STATUS_OPTIONS = STATUS_OPTIONS_DATA.map(option => ({
  ...option,
  icon: ICON_MAP[option.iconName] || Package,
}));

export function EnhancedLivreurStatusManager({
  packageData,
  trigger,
  onUpdate,
}) {
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [note, setNote] = useState("");
  const [isQuickAction, setIsQuickAction] = useState(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Calculate delivery progress
  const currentStatusOption = ENHANCED_STATUS_OPTIONS.find(
    (option) => option.value === packageData.status
  );
  const selectedStatusOption = ENHANCED_STATUS_OPTIONS.find(
    (option) => option.value === selectedStatus
  );

  const getDeliveryProgress = () => {
    const statusOrder = currentStatusOption?.order || 0;
    return Math.min((statusOrder / 8) * 100, 100);
  };

  // Get available next steps based on current status
  const getAvailableStatuses = () => {
    if (!currentStatusOption?.nextSteps) {
      return ENHANCED_STATUS_OPTIONS;
    }
    return ENHANCED_STATUS_OPTIONS.filter((option) =>
      currentStatusOption.nextSteps.includes(option.value)
    );
  };

  // Update status mutation with enhanced error handling
  const updateStatusMutation = useMutation({
    mutationFn: ({ status, note }) =>
      packagesService.updatePackageStatusByLivreur(packageData._id, {
        status,
        note,
      }),
    onSuccess: (data) => {
      // Invalidate multiple query keys for comprehensive updates
      const queriesToInvalidate = [
        [PACKAGE_QUERY_KEYS.BASE],
        [PACKAGE_QUERY_KEYS.DETAIL, packageData._id],
        [PACKAGE_QUERY_KEYS.LIVREUR],
        [PACKAGE_QUERY_KEYS.LIVREUR_DASHBOARD],
        ["dashboard"],
      ];

      queriesToInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });

      toast.success(t("Package status updated successfully"), {
        description: `Status changed to ${t(selectedStatusOption?.label)}`,
      });

      setOpen(false);
      setSelectedStatus("");
      setNote("");
      setIsQuickAction(false);

      // Call onUpdate callback if provided
      onUpdate?.(data);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.message || t("Failed to update package status");
      toast.error(t("Update Failed"), {
        description: errorMessage,
      });
    },
  });

  const handleQuickAction = (actionKey) => {
    const action = QUICK_ACTIONS_DATA[actionKey];
    setSelectedStatus(action.status);
    setNote(t(action.noteKey));
    setIsQuickAction(true);
  };

  const handleUpdateStatus = () => {
    if (!selectedStatus) {
      toast.error(t("Please select a status"));
      return;
    }

    if (selectedStatus === packageData.status) {
      toast.error(t("Please select a different status"));
      return;
    }

    updateStatusMutation.mutate({ status: selectedStatus, note: note.trim() });
  };

  const formatDateTime = (date) => {
    return format(new Date(date), "MMM dd, yyyy HH:mm");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("Update Delivery Status")}
          </DialogTitle>
          <DialogDescription>
            {t("Manage the delivery status for package")} #
            {packageData._id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Package Overview Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{t("Package Information")}</span>
                <Badge variant="outline" className="flex items-center gap-1">
                  {currentStatusOption?.icon && (
                    <currentStatusOption.icon className="h-3 w-3" />
                  )}
                  {t(currentStatusOption?.label || packageData.status)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Delivery Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t("Delivery Progress")}</span>
                  <span>{Math.round(getDeliveryProgress())}%</span>
                </div>
                <Progress value={getDeliveryProgress()} className="h-2" />
              </div>

              {/* Customer Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {packageData.customer.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{packageData.customer.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {packageData.customer.address},{" "}
                      {packageData.customer.city}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {t("Created")}: {formatDateTime(packageData.createdAt)}
                    </span>
                  </div>
                  {packageData.trackingNumber && (
                    <div className="text-sm">
                      <span className="font-medium">{t("Tracking")}:</span>{" "}
                      {packageData.trackingNumber}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("Quick Actions")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("successful_delivery")}
                  className="justify-start"
                >
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  {t("Successful Delivery")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("customer_not_home")}
                  className="justify-start"
                >
                  <Clock className="h-4 w-4 mr-2 text-orange-500" />
                  {t("Customer Not Home")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("wrong_address")}
                  className="justify-start"
                >
                  <MapPin className="h-4 w-4 mr-2 text-blue-500" />
                  {t("Wrong Address")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("customer_refused")}
                  className="justify-start"
                >
                  <XCircle className="h-4 w-4 mr-2 text-red-500" />
                  {t("Customer Refused")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Status Selection */}
          <div className="space-y-3">
            <Label htmlFor="status" className="text-base font-medium">
              {t("Select New Status")}
            </Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t("Choose delivery status")} />
              </SelectTrigger>
              <SelectContent>
                {getAvailableStatuses().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${option.color}`} />
                      <option.icon className="h-4 w-4" />
                      <div className="flex flex-col">
                        <span className="font-medium">{t(option.label)}</span>
                        <span className="text-xs text-muted-foreground">
                          {t(option.description)}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Preview */}
          {selectedStatusOption && (
            <Card className="border-2 border-dashed">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div
                    className={`w-4 h-4 rounded-full ${selectedStatusOption.color} mt-0.5`}
                  />
                  <div className="space-y-1">
                    <div className="font-medium">
                      {t(selectedStatusOption.label)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t(selectedStatusOption.description)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Note */}
          <div className="space-y-3">
            <Label htmlFor="note" className="text-base font-medium">
              {t("Delivery Notes")}{" "}
              {isQuickAction && (
                <span className="text-sm text-muted-foreground">
                  ({t("pre-filled")})
                </span>
              )}
            </Label>
            <Textarea
              id="note"
              placeholder={t(
                "Add details about the delivery attempt, customer interaction, or any issues encountered..."
              )}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <div className="text-xs text-muted-foreground">
              {t(
                "Adding detailed notes helps improve future deliveries and customer service"
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              setSelectedStatus("");
              setNote("");
              setIsQuickAction(false);
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleUpdateStatus}
            disabled={
              updateStatusMutation.isPending ||
              !selectedStatus ||
              selectedStatus === packageData.status
            }
            className="min-w-[120px]"
          >
            {updateStatusMutation.isPending ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t("Updating...")}
              </div>
            ) : (
              t("Update Status")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EnhancedLivreurStatusManager;
